import { FC, useState, MouseEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import RejectDialog from 'src/components/DialogBox/RejectDialog/RejectDialog';
import ViewOrderDialog from 'src/components/DialogBox/ViewOrderDialog/ViewOrderDialog';
import {
  actionButtons,
  apiUrls,
  orderDialogTitles,
  orderDialogSubtitles,
  routeURL,
  userRoles,
  dataStorageKeywords,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { OrderStatus } from 'src/models/enums/OrderStatus.enum';
import { OrderViewData } from 'src/models/Management.interface';
import UpdateDeliveryDialog from 'src/components/DialogBox/UpdateDeliveryDialog/UpdateDeliveryDialog';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import Icon from 'src/assets/icons/Icon';
import { generateInvoicePDF } from 'src/components/Invoice/InvoicePDF';

const OrderActionMenuButton: FC<{
  rowId: string;
  orderNumber: string;
  status: OrderStatus;
  fetchOrders: () => void;
  deleteOrder: (id: string) => void;
}> = ({ rowId, orderNumber, status, fetchOrders, deleteOrder }) => {
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderViewData | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  // Determine which actions to show based on order status
  const showEdit = status === OrderStatus.NEW || status === OrderStatus.PENDING;
  const showDelete =
    status === OrderStatus.NEW || status === OrderStatus.PENDING;
  // Only Admin and Owner can approve/reject orders in Pending status
  const isAdminOrOwner =
    userRole === userRoles.ADMIN || userRole === userRoles.OWNER;
  const showApprove = status === OrderStatus.PENDING && isAdminOrOwner;
  const showReject = status === OrderStatus.PENDING && isAdminOrOwner;
  // Update delivery for Approved, Payment Pending, and a possible "Payment Completed" string
  const showUpdateDelivery =
    status === OrderStatus.APPROVED ||
    status === OrderStatus.PAYMENT_PENDING ||
    status === OrderStatus.PAYMENT_COMPLETED;

  // Show Order Payments for Approved, Payment Pending, Payment Completed, and Completed
  const showOrderPayments =
    status === OrderStatus.APPROVED ||
    status === OrderStatus.PAYMENT_PENDING ||
    status === OrderStatus.PAYMENT_COMPLETED ||
    status === OrderStatus.COMPLETED;

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleView = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_ORDER_BY_ID}/${rowId}`
      );
      setSelectedOrder(response.data);
      setViewDialogOpen(true);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEdit = () => {
    handleClose();
    navigate(routeURL.ORDER_EDIT_REDIRECT(rowId));
  };

  const handleApprove = () => {
    handleClose();
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    try {
      const response = await axiosTokenInstance.patch(`orders/${rowId}`, {
        status: 'Approved',
      });
      showSnackbar(
        response.data.message || 'Order approved successfully',
        'success'
      );
      fetchOrders();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveDialogOpen(false);
    }
  };

  const handleReject = () => {
    handleClose();
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const response = await axiosTokenInstance.patch(`orders/${rowId}`, {
        status: 'Rejected',
        notes: reason,
      });
      showSnackbar(
        response.data.message || 'Order rejected successfully',
        'success'
      );
      fetchOrders();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectDialogOpen(false);
    }
  };

  const handleUpdateDeliveryDetailsClick = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_ORDER_BY_ID}/${rowId}`
      );
      setSelectedOrder(response.data);
      setDeliveryDialogOpen(true);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleOrderPayments = () => {
    handleClose();
    navigate(routeURL.ORDER_PAYMENTS_REDIRECT(rowId), {
      state: { orderNumber },
    });
  };

  const handleDeleteClick = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteOrder(rowId);
    setDeleteDialogOpen(false);
  };

  const handlePrintInvoice = async () => {
    handleClose();
    setIsGeneratingPDF(true);
    try {
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_ORDER_BY_ID}/${rowId}`
      );
      await generateInvoicePDF(response.data);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleCloseDeliveryDialog = () => {
    setDeliveryDialogOpen(false);
    setSelectedOrder(null);
  };

  return (
    <>
      <ActionMenuButton
        id="action-menu-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <i className="bx bx-dots-horizontal-rounded"></i>
      </ActionMenuButton>

      <ActionMenuPopover
        id="action-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleView}
            iconClassName="bx bx-file-find"
            text={actionButtons.VIEW}
          />

          {showEdit && (
            <MenuItemWithIcon
              onClick={handleEdit}
              iconClassName="bx bx-edit"
              text={actionButtons.EDIT}
            />
          )}

          {(showApprove || showReject) && (
            <>
              {showApprove && (
                <MenuItemWithIcon
                  onClick={handleApprove}
                  iconClassName="bx bx-check"
                  text={actionButtons.APPROVE}
                />
              )}

              {showReject && (
                <MenuItemWithIcon
                  onClick={handleReject}
                  iconClassName="bx bx-x"
                  text={actionButtons.REJECT}
                />
              )}
            </>
          )}

          {showUpdateDelivery && (
            <MenuItemWithIcon
              onClick={handleUpdateDeliveryDetailsClick}
              icon={
                <Icon
                  name="InvoiceDollar"
                  size={16}
                  color="var(--primary-navy)"
                />
              }
              text="Update Delivery"
            />
          )}

          {showOrderPayments && (
            <MenuItemWithIcon
              onClick={handleOrderPayments}
              iconClassName="bx bx-dollar-circle"
              text="Order Payments"
            />
          )}

          {showDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteClick}
              iconClassName="bx bx-trash"
              text={actionButtons.DELETE}
            />
          )}

          <MenuItemWithIcon
            onClick={handlePrintInvoice}
            iconClassName="bx bx-printer"
            text={isGeneratingPDF ? 'Generating...' : 'Print Invoice'}
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ViewOrderDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        data={selectedOrder}
        onApprove={fetchOrders}
        onReject={fetchOrders}
        onOrderPayments={() => {
          handleClose();
          navigate(routeURL.ORDER_PAYMENTS_REDIRECT(rowId), {
            state: { orderNumber: selectedOrder?.orderNumber || orderNumber },
          });
        }}
      />

      <UpdateDeliveryDialog
        open={deliveryDialogOpen}
        onClose={handleCloseDeliveryDialog}
        rowId={rowId}
        data={selectedOrder}
        onSuccess={fetchOrders}
      />

      <ConfirmationDialog
        height="275px"
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={orderDialogTitles.APPROVE}
        subtitle={orderDialogSubtitles.APPROVE}
        confirmText="Approve"
        confirmButtonColor="var(--success)"
        alertIconColor="var(--success)"
      />

      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title="Reject Order"
        subtitle="Rejection Note"
        subtitleSecondary=""
        placeholder="Reason for rejection"
        confirmText="Reject"
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={orderDialogTitles.DELETE}
        subtitle={orderDialogSubtitles.DELETE}
        confirmText="Delete"
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
    </>
  );
};

export default OrderActionMenuButton;
